/**
 * Route admin `/tutor/admin/trousses` — les 32 trousses du catalogue.
 *
 * La grille montre les couvertures 2D (nom, rang, rareté, forme 3D, exemplaires distribués). Toucher
 * une trousse ouvre une fenêtre agrandie avec la trousse en 3D : on la fait tourner en la glissant, un
 * double clic (ou Entrée) l'ouvre ou la ferme. Une seule scène WebGL à la fois.
 *
 * Dans cette fenêtre l'admin choisit le MODÈLE (boîte, berlingot, tube — `trousses.shape`, 0745),
 * télécharge le PATRON de ce modèle (une seule image, une zone par face), puis importe l'image peinte
 * (bucket public `trousse-skins`, `trousses.skin_path`) : la 3D l'applique aussitôt, partout.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Download, Package, Trash2, Upload, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  TROUSSE_SHAPES,
  TROUSSE_SKIN_BUCKET,
  Trousse3D,
  invalidateTrousseLooks,
  patronCanvas,
  patronUrl,
  shapeForSlug,
  type TrousseShape,
} from '@/features/trousse-3d';
import '@/features/trousse-3d/trousse-3d.css';
import { formatCopies, trousseImage, trousseRarity, trousseRarityColor } from '@/lib/trousses';

const db = supabase as unknown as SupabaseClient;

interface CatalogRow {
  slug: string;
  name: string;
  rank: number;
  copies: number;
  taken: number;
  image_path: string;
  is_default: boolean;
  /** forme choisie par l'admin ; null = tirage de `shapes.ts` */
  shape: TrousseShape | null;
  /** patron peint (bucket `trousse-skins`) ; null = la couverture 2D */
  skin_path: string | null;
}

const CATALOG_KEY = ['admin', 'trousse-catalog'] as const;

/** La forme affichée : celle de l'admin, sinon le tirage. */
function shapeOf(t: CatalogRow): TrousseShape {
  return t.shape ?? shapeForSlug(t.slug);
}

const SHAPE_LABEL: Record<TrousseShape, string> = {
  boite: 'Boîte',
  berlingot: 'Berlingot',
  tube: 'Tube',
};

function useTrousseCatalog() {
  return useQuery({
    queryKey: CATALOG_KEY,
    queryFn: async (): Promise<CatalogRow[]> => {
      const { data, error } = await db
        .from('trousses')
        .select('slug, name, rank, copies, taken, image_path, is_default, shape, skin_path')
        .order('rank');
      if (error) throw new Error(error.message);
      return (data ?? []) as CatalogRow[];
    },
    staleTime: 60_000,
  });
}

function rarityLabel(copies: number): string {
  const r = trousseRarity(copies);
  return r.charAt(0).toUpperCase() + r.slice(1);
}

function TrousseMeta({ t }: { t: CatalogRow }) {
  return (
    <div className="space-y-1">
      <p className="font-display text-ink-900 flex items-center gap-1.5 text-[15px] font-bold">
        {t.name}
        {t.is_default && (
          <span className="bg-primary-50 text-primary-700 rounded-full px-1.5 py-0.5 font-sans text-[11px] font-semibold">
            de base
          </span>
        )}
      </p>
      <p className="text-ink-500 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-sans text-[12px]">
        <span className="font-semibold" style={{ color: trousseRarityColor(t.copies) }}>
          {rarityLabel(t.copies)}
        </span>
        <span>· {SHAPE_LABEL[shapeOf(t)]}</span>
        {t.skin_path && <span>· patron</span>}
        <span>· {t.slug}</span>
      </p>
      <p className="text-ink-500 font-sans text-[12px]">
        {formatCopies(t.taken)} distribuée{t.taken > 1 ? 's' : ''} sur {formatCopies(t.copies)}
      </p>
    </div>
  );
}

function TrousseTile({ t, onSelect }: { t: CatalogRow; onSelect: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-label={`Voir ${t.name} en 3D`}
        className="border-ink-100 ocean:bg-[#0b1a40] block w-full overflow-hidden rounded-2xl border bg-white text-left transition active:scale-[0.98]"
      >
        <div className="relative h-48 bg-[radial-gradient(circle_at_50%_40%,#1f3570_0%,#061433_75%)]">
          <img
            src={trousseImage(t.image_path)}
            alt=""
            className="h-full w-full object-contain p-4"
            draggable={false}
          />
          <span className="absolute left-2 top-2 rounded-full bg-[rgba(6,20,51,0.75)] px-2 py-0.5 font-sans text-[12px] font-semibold text-[#ffffff]">
            #{t.rank}
          </span>
        </div>
        <div className="px-3 py-2.5">
          <TrousseMeta t={t} />
        </div>
      </button>
    </li>
  );
}

function TrousseViewer({ t, onClose }: { t: CatalogRow; onClose: () => void }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t.name}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(3,8,22,0.82)] p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        className="ocean:bg-[#0b1a40] flex h-full w-full max-w-5xl flex-col overflow-hidden bg-white sm:h-auto sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-4 pb-2 pt-[max(1rem,env(safe-area-inset-top))]">
          <TrousseMeta t={t} />
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-ink-500 hover:text-ink-900 flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-pressed={open}
          aria-label={`${t.name} : ${open ? 'fermer' : 'ouvrir'} la trousse (double clic)`}
          onDoubleClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((o) => !o);
            }
          }}
          className="relative min-h-0 flex-1 cursor-grab select-none bg-[radial-gradient(circle_at_50%_40%,#1f3570_0%,#061433_75%)] active:cursor-grabbing sm:h-[65vh] sm:flex-none"
        >
          <Trousse3D
            slug={t.slug}
            image={trousseImage(t.image_path)}
            shape={shapeOf(t)}
            patron={t.skin_path ? patronUrl(t.skin_path) : null}
            open={open}
            spin={false}
          >
            <img
              src={trousseImage(t.image_path)}
              alt=""
              className="h-full w-full object-contain p-8"
              draggable={false}
            />
          </Trousse3D>
          <span className="absolute right-3 top-3 rounded-full bg-[rgba(6,20,51,0.75)] px-2.5 py-1 font-sans text-[12px] font-semibold text-[#ffffff]">
            {open ? 'Ouverte' : 'Fermée'}
          </span>
        </div>
        <p className="text-ink-500 px-4 pt-3 text-center font-sans text-[13px]">
          Glisse pour la faire tourner · double-clique pour l’ouvrir ou la fermer
        </p>
        <LookControls t={t} />
      </div>
    </div>,
    document.body,
  );
}

const PATRON_TYPES: Record<string, 'png' | 'jpg' | 'webp'> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
};
const PATRON_MAX_BYTES = 8 * 1024 * 1024;

function downloadPatron(shape: TrousseShape): void {
  patronCanvas(shape).toBlob((blob) => {
    if (!blob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `patron-trousse-${shape}.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }, 'image/png');
}

/** Modèle 3D et patron peint d'une trousse : enregistrés aussitôt, appliqués partout. */
function LookControls({ t }: { t: CatalogRow }) {
  const qc = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const refresh = () => {
    invalidateTrousseLooks();
    return qc.invalidateQueries({ queryKey: CATALOG_KEY });
  };

  const setShape = useMutation({
    mutationFn: async (shape: TrousseShape | null) => {
      const { error } = await db.from('trousses').update({ shape }).eq('slug', t.slug);
      if (error) throw new Error(error.message);
    },
    onSettled: refresh,
  });

  const setPatron = useMutation({
    mutationFn: async (f: File | null) => {
      let path: string | null = null;
      if (f) {
        const ext = PATRON_TYPES[f.type];
        if (!ext) throw new Error('Image PNG, JPEG ou WebP seulement.');
        if (f.size > PATRON_MAX_BYTES) throw new Error('Image trop lourde (8 Mo au plus).');
        path = `${t.slug}/${crypto.randomUUID()}.${ext}`;
        const up = await db.storage
          .from(TROUSSE_SKIN_BUCKET)
          .upload(path, f, { contentType: f.type, upsert: false });
        if (up.error) throw new Error(up.error.message);
      }
      const { error } = await db.from('trousses').update({ skin_path: path }).eq('slug', t.slug);
      if (error) {
        if (path) await db.storage.from(TROUSSE_SKIN_BUCKET).remove([path]);
        throw new Error(error.message);
      }
      // l'ancien patron ne sert plus (au mieux : un échec ici ne change rien à l'écran)
      if (t.skin_path && t.skin_path !== path) {
        await db.storage.from(TROUSSE_SKIN_BUCKET).remove([t.skin_path]);
      }
    },
    onSettled: refresh,
  });

  const shape = shapeOf(t);
  const busy = setShape.isPending || setPatron.isPending;
  const error = setShape.error ?? setPatron.error;

  return (
    <div className="space-y-4 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div>
        <p className="text-ink-900 mb-2 font-sans text-[13px] font-semibold">
          Modèle de trousse
          {!t.shape && <span className="text-ink-500 font-normal"> · tiré au sort</span>}
        </p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Modèle de trousse">
          {TROUSSE_SHAPES.map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={shape === s}
              disabled={busy}
              onClick={() => setShape.mutate(s)}
              className={`min-h-11 rounded-full border px-4 font-sans text-[14px] font-semibold transition active:scale-[0.98] ${
                shape === s
                  ? 'border-primary-500 bg-primary-500 text-white'
                  : 'border-ink-200 text-ink-700'
              }`}
            >
              {SHAPE_LABEL[s]}
            </button>
          ))}
          {t.shape && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setShape.mutate(null)}
              className="text-primary-600 min-h-11 px-2 font-sans text-[13px] font-semibold"
            >
              Revenir au tirage ({SHAPE_LABEL[shapeForSlug(t.slug)]})
            </button>
          )}
        </div>
      </div>

      <div>
        <p className="text-ink-900 mb-2 font-sans text-[13px] font-semibold">
          Patron du tissu
          <span className="text-ink-500 font-normal">
            {t.skin_path ? ' · patron importé' : ' · couverture 2D'}
          </span>
        </p>
        <p className="text-ink-500 mb-2 font-sans text-[12px]">
          Télécharge le patron du modèle {SHAPE_LABEL[shape].toLowerCase()} (une image, une zone par
          face), peins-le en gardant ses proportions, puis importe-le.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => downloadPatron(shape)}
            className="border-ink-200 text-ink-700 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border px-4 font-sans text-[14px] font-semibold active:scale-[0.98]"
          >
            <Download className="h-4 w-4" /> Télécharger le patron
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => file.current?.click()}
            className="bg-primary-500 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 font-sans text-[14px] font-semibold text-white active:scale-[0.98] disabled:opacity-60"
          >
            <Upload className="h-4 w-4" />
            {setPatron.isPending ? 'Import…' : 'Importer l’image peinte'}
          </button>
          {t.skin_path && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setPatron.mutate(null)}
              className="text-accent-600 inline-flex min-h-11 items-center justify-center gap-2 px-3 font-sans text-[14px] font-semibold"
            >
              <Trash2 className="h-4 w-4" /> Retirer le patron
            </button>
          )}
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            data-testid="patron-file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) setPatron.mutate(f);
            }}
          />
        </div>
      </div>
      {error && <p className="text-accent-600 font-sans text-[13px]">{error.message}</p>}
    </div>
  );
}

export function TutorAdminTroussesPage() {
  const catalog = useTrousseCatalog();
  const rows = catalog.data ?? [];
  const [selected, setSelected] = useState<CatalogRow | null>(null);
  const closeViewer = useCallback(() => setSelected(null), []);
  const byShape = (s: TrousseShape) => rows.filter((r) => shapeOf(r) === s).length;
  // la fenêtre suit la ligne à jour (après un changement de modèle ou de patron)
  const live = selected ? (rows.find((r) => r.slug === selected.slug) ?? selected) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
      <h1 className="font-display text-ink-900 flex items-center gap-2 text-2xl font-extrabold">
        <Package className="h-6 w-6" /> Trousses
      </h1>
      <p className="text-ink-500 mt-1 font-sans text-sm">
        Touche une trousse pour la voir en 3D, en grand : glisse pour la faire tourner,
        double-clique pour l’ouvrir ou la fermer.
        {rows.length > 0 &&
          ` ${rows.length} trousses : ${byShape('boite')} boîtes, ${byShape('berlingot')} berlingots, ${byShape('tube')} tubes.`}
      </p>

      {catalog.isLoading && <p className="text-ink-500 mt-6 font-sans text-sm">Chargement…</p>}
      {catalog.isError && (
        <p className="text-accent-600 mt-6 font-sans text-sm">{catalog.error.message}</p>
      )}

      <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((t) => (
          <TrousseTile key={t.slug} t={t} onSelect={() => setSelected(t)} />
        ))}
      </ul>
      {live && <TrousseViewer t={live} onClose={closeViewer} />}
    </div>
  );
}
